import React, { useState, useEffect, useMemo, useCallback } from "react";
import { request } from "../../../utils/api";
import { getSessionTenantId } from "../../../utils/tenantContext";
import { toast } from "react-hot-toast";
import { SearchIcon, CloseIcon, ChevronDownIcon } from "../../../Icons";
import Table from "../../../components/UI/Table";
import { displayDateUS } from "../../../utils/dateUtils";
import { CELL, CELL_MUTED, CELL_STRONG } from "../../../ui/pageChrome";
import {
  REPORT_THEME,
  REPORT_BTN,
  REPORT_FIELD,
  REPORT_ROLE_CHIP,
} from "../reportTheme";

const ExpiredCredentialsTable = () => {
  const [data, setData] = useState({
    items: [],
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const itemsPerPage = 20;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchExpiredCredentials = useCallback(
    async (pageNum) => {
      setLoading(true);
      try {
        const tenantId = getSessionTenantId();
        if (!tenantId) {
          toast.error("Tenant context is missing. Please sign in again.");
          return;
        }

        let url = `/reports/credentials/expired?page=${pageNum}&limit=${itemsPerPage}&tenantId=${tenantId}`;
        if (debouncedSearch) {
          url += `&search=${encodeURIComponent(debouncedSearch)}`;
        }

        const response = await request(url);
        if (response.status === 1 && response.result) {
          setData({
            items: response.result.items || [],
            total: response.result.total || 0,
            totalPages: response.result.totalPages || 1,
            page: response.result.page || 1,
            limit: response.result.limit || itemsPerPage,
          });
        } else {
          toast.error(
            response.message || "Failed to fetch expired credentials",
          );
          setData({
            items: [],
            total: 0,
            totalPages: 0,
            page: 1,
            limit: itemsPerPage,
          });
        }
      } catch (err) {
        console.error("Expired credentials fetch error:", err);
        toast.error("Error loading expired credentials");
        setData({
          items: [],
          total: 0,
          totalPages: 0,
          page: 1,
          limit: itemsPerPage,
        });
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch],
  );

  useEffect(() => {
    fetchExpiredCredentials(page);
  }, [page, fetchExpiredCredentials]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= data.totalPages) {
      setPage(newPage);
    }
  };

  const roleOptions = useMemo(() => {
    const roles = new Set();
    (data.items || []).forEach((row) => {
      if (row.role) roles.add(String(row.role));
    });
    return [
      { value: "", label: "All Roles" },
      ...Array.from(roles)
        .sort((a, b) => a.localeCompare(b))
        .map((role) => ({ value: role, label: role })),
    ];
  }, [data.items]);

  const hasActiveFilters = Boolean(searchTerm.trim() || roleFilter);

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
  };

  const filteredItems = useMemo(() => {
    if (!roleFilter) return data.items || [];
    return (data.items || []).filter(
      (row) => String(row.role || "") === roleFilter,
    );
  }, [data.items, roleFilter]);

  const columns = useMemo(
    () => [
      {
        header: "Employee",
        accessor: "employeeName",
        className: "min-w-[9rem] px-2 align-middle",
        render: (row) => (
          <div className="min-w-0">
            <div
              className={`${CELL_STRONG} capitalize text-slate-900 truncate`}
              title={row.employeeName || ""}
            >
              {row.employeeName || "—"}
            </div>
            <div
              className={`${CELL_MUTED} mt-0.5 truncate`}
              title={row.employeeEmail || ""}
            >
              {row.employeeEmail || "—"}
            </div>
          </div>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        className: "whitespace-nowrap px-2 align-middle w-[4.5rem]",
        render: (row) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${REPORT_ROLE_CHIP}`}
          >
            {row.role || "—"}
          </span>
        ),
      },
      {
        header: "Credential",
        accessor: "displayName",
        className: "min-w-[10rem] px-2 align-middle",
        render: (row) => (
          <span
            className={`${CELL} text-slate-900 truncate block`}
            title={row.displayName || ""}
          >
            {row.displayName || "—"}
          </span>
        ),
      },
      {
        header: "Expiry Date",
        accessor: "expireAt",
        className: "whitespace-nowrap pl-2 pr-3 align-middle w-[7.5rem]",
        render: (row) => (
          <span className="inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-red-50 text-red-700 border-red-200 tabular-nums">
            {displayDateUS(row.expireAt) || "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
        <div className="relative flex-1 min-w-0">
          <SearchIcon
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by name, email..."
            className={REPORT_FIELD.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm ? (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <CloseIcon size={14} />
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative">
            <label className="sr-only" htmlFor="expired-role-filter">
              Filter by role
            </label>
            <select
              id="expired-role-filter"
              className={`${REPORT_FIELD.select} ${
                roleFilter ? REPORT_FIELD.selectActive : ""
              }`}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              {roleOptions.map((option) => (
                <option key={option.value || "all-roles"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>

          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className={REPORT_BTN.clear}
            >
              Clear
            </button>
          ) : null}

          <span
            className={`h-8 inline-flex items-center px-2.5 rounded-md text-[11px] font-semibold tabular-nums ${REPORT_THEME.section.badge}`}
          >
            {filteredItems.length}
            {hasActiveFilters ? ` / ${data.total}` : ""} credentials
          </span>
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredItems}
        loading={loading}
        keyField="id"
        showSrNo
        density="compact"
        embedded
        tableClassName="table-fixed"
        srNoClassName="w-10 whitespace-nowrap pl-3 pr-1 text-center align-middle"
        headTone={REPORT_THEME.tableHead}
        pagination={{
          currentPage: page,
          totalPages: data.totalPages,
          onPageChange: handlePageChange,
          totalItems: data.total,
          itemsPerPage: data.limit || itemsPerPage,
        }}
        noDataMessage="No expired credentials found."
      />
    </>
  );
};

export default ExpiredCredentialsTable;
